import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { axiosCall } from "../utils/axiosCall";

import constants from "../config/constants";
import { useDispatch } from "react-redux";
import {
  setEmail,
  setVerificationType,
  userLogin,
} from "../redux/features/userSlice";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loginAttempts, setLoginAttempts] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrorMessage("");
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setErrorMessage("Please fill out all details.");
      return;
    }

    try {
      const login = await axiosCall(
        constants.POST_METHOD,
        constants.LOGIN,
        {
          emailOrPhone: formData.email,
          password: formData.password,
        },
        null,
        null
      );

      if (login.isSuccess) {
        if (login.data?.data?.token) {
          dispatch(userLogin(login?.data?.data));
          navigate("/patient");
        } else {
          dispatch(setVerificationType("LOGIN"));
          dispatch(setEmail(formData.email));
          navigate("/verification");
        }
      } else {
        setErrorMessage(login.error.message);
      }
    } catch (error) {
      console.log(error);
    }

    // Simulating authentication logic
    // setLoginAttempts((prevAttempts) => prevAttempts + 1);
    // if (loginAttempts >= 2) {
    //   setErrorMessage(
    //     'Account locked due to multiple failed login attempts. Please contact the system administrator.'
    //   );
    // } else {
    //   setErrorMessage('Incorrect username or password. Please try again.');
    // }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setErrorMessage("Please fill email.");
      return;
    }

    try {
      const forgotPassword = await axiosCall(
        constants.POST_METHOD,
        constants.FORGOT_PASSWORD,
        { emailOrPhone: formData.email },
        null,
        null
      );

      if (forgotPassword.isSuccess) {
        dispatch(setVerificationType("FPASSWORD"));
        dispatch(setEmail(formData.email));
        navigate("/verification");
      } else {
        setErrorMessage(forgotPassword.error.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box display="flex" height="100vh">
      {/* Left Image Section */}
      <Box width="50%" overflow="hidden">
        <img
          src="/Images/leftlogo.png"
          alt="Doctor holding scalpel"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Right Content Section */}
      <Box
        maxWidth={"50%"}
        p={5}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        ml={5}
      >
        {/* Logo and Text */}
        <Box display="flex" alignItems="center" mb={3} mt={3}>
          <img
            src="/Images/plus.png"
            alt="Plus sign"
            style={{ width: 40, height: 40, marginRight: 12, marginTop: -20 }}
          />
          <Box ml={2} mb={-3} fontSize={12} lineHeight={1.2}>
            <Typography
              variant="h6"
              color="#00C6F2"
              fontWeight="bold"
              style={{ marginTop: -40 }}
            >
              HOSPITAL MANAGEMENT
            </Typography>
            <Typography variant="h6" color="textSecondary" fontWeight={600}>
              INFORMATION SYSTEM
            </Typography>
          </Box>
        </Box>

        {/* Login Form */}
        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          mt={-7}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Welcome to
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="textPrimary"
            gutterBottom
          >
            HMIS PORTAL
          </Typography>
          <Typography
            variant="h6"
            color="#00C6F2"
            fontWeight="bold"
            mb={6}
            fontFamily="Helvetica"
            gutterBottom
          >
            HOSPITAL LOGO
          </Typography>

          <form onSubmit={handleNext} style={{ width: "30rem" }}>
            {/* Email Input */}
            <Box mb={2} width="100%">
              <Box
                component="label"
                htmlFor="email"
                sx={{ color: "#666", display: "block", mb: 1 }}
              >
                Enter Email / Mobile Number
              </Box>
              <TextField
                fullWidth
                id="email"
                name="email"
                variant="outlined"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="name@example.com"
                required
              />
            </Box>

            {/* Password Input */}
            <Box mb={2} width="100%" sx={{ position: "relative" }}>
              <Box
                component="label"
                htmlFor="password"
                sx={{ color: "#666", display: "block", mb: 1 }}
              >
                Enter Password
              </Box>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                variant="outlined"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </IconButton>
                  ),
                }}
                required
              />
            </Box>

            {/* Error Message */}
            {errorMessage && (
              <Typography color="error" variant="body2" mb={2}>
                {errorMessage}
              </Typography>
            )}

            {/* Forgot Password */}
            <Box mt={4} mb={2}>
              <Typography variant="body2">
                <a
                  href="#"
                  onClick={handleForgotPassword}
                  style={{ color: "#0077be", textDecoration: "none" }}
                >
                  Forgot Password?
                </a>
              </Typography>
            </Box>

            {/* Submit Button */}
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Next
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
